import React, { useMemo } from 'react'
import { useAnalyticsMetric } from '../hooks/useAnalyticsMetric'
import { Area, AreaChart } from 'recharts'

import { ChartContainer, type ChartConfig } from '@bpinternal/ui-kit-next'

type Props = {
  botId: string
  workspaceId: string
  color?: 1 | 2 | 3 | 4 | 5
  size?: 'default' | 'compact'
}

export const MessagesAnalyticsWidget = ({ botId, workspaceId, color = 1, size = 'default' }: Props) => {
  const analyticsData = useAnalyticsMetric(botId, workspaceId, {
    extractValue: (record) => {
      if (record && typeof record === 'object' && 'messages' in record) {
        return typeof record.messages === 'number' ? record.messages : 0
      }
      return 0
    },
    chartDataKey: 'totalMessages',
    valueKey: 'last7DaysMessages',
  })

  const { last7DaysMessages, deltaFormatted, chartData } = analyticsData

  const isCompact = size === 'compact'

  const allDataZero = useMemo(() => {
    return chartData.every((item) => item.totalMessages === 0 || item.totalMessages === undefined)
  }, [chartData])

  const allLastWeekDataZero = useMemo(() => {
    return chartData.every((item) => item.totalMessagesLastWeek === 0 || item.totalMessagesLastWeek === undefined)
  }, [chartData])

  const chartConfig: ChartConfig = {
    totalMessages: {
      color: allDataZero ? 'hsl(var(--chart-gray))' : `hsl(var(--chart-${color}))`,
    },
    totalMessagesLastWeek: {
      color: allLastWeekDataZero ? 'hsl(var(--chart-gray))' : `hsl(var(--chart-${color}))`,
    },
  }

  return (
    <div className={`${isCompact ? 'h-12' : 'h-24 sm:h-20'} relative w-full min-w-0`}>
      {!isCompact && (
        <div className="truncate text-xs font-medium">
          {last7DaysMessages.toLocaleString()}
          {' Messages'}
        </div>
      )}
      <div className="truncate text-xs text-muted-foreground">{deltaFormatted}</div>
      <ChartContainer config={chartConfig} className={`${isCompact ? 'h-12' : 'h-20'} w-full min-w-0`}>
        <AreaChart accessibilityLayer data={chartData} margin={{ top: 16, bottom: 16 }}>
          <defs>
            <linearGradient id={`fillMessages-${botId}`} x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="5%"
                stopColor={allDataZero ? 'hsl(var(--chart-gray))' : 'var(--color-totalMessages)'}
                stopOpacity={0.8}
              />
              <stop
                offset="95%"
                stopColor={allDataZero ? 'hsl(var(--chart-gray))' : 'var(--color-totalMessages)'}
                stopOpacity={0.1}
              />
            </linearGradient>
          </defs>
          <Area
            dataKey="totalMessages"
            type="bump"
            strokeWidth={1.5}
            fill={`url(#fillMessages-${botId})`}
            fillOpacity={0.4}
            stroke={allDataZero ? 'hsl(var(--chart-gray))' : 'var(--color-totalMessages)'}
            stackId="a"
          />
          <Area
            dataKey="totalMessagesLastWeek"
            type="bump"
            strokeWidth={1.5}
            strokeDasharray="3 3"
            fill="none"
            fillOpacity={0}
            stroke={allLastWeekDataZero ? 'hsl(var(--chart-gray))' : 'var(--color-totalMessagesLastWeek)'}
            strokeOpacity={0.6}
            stackId="b"
          />
        </AreaChart>
      </ChartContainer>
    </div>
  )
}
